import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

import { Button, ButtonGroup, Modal, Segmented } from "../../../components/ui";

import {
  formatMoney as formatMoneyShared,
  symbolFor,
} from "../../../core/currency";

import "./SendPaymentLinkModal.scss";

const EXPIRY_OPTIONS = [
  { value: 1,  label: "24 hours" },
  { value: 3,  label: "3 days" },
  { value: 7,  label: "7 days" },
  { value: 14, label: "14 days" },
];

// Whole-unit money (rounded, no decimals) — delegates to the shared
// multi-currency formatter; the symbol follows the currency code.
const formatMoney = (pence, currency = "GBP") =>
  formatMoneyShared(pence, { currency, decimals: 0 });

// Quick-fill shares of the remaining balance (Segmented values are strings).
const QUICK_FILLS = [
  { value: "1", factor: 1, label: "Full" },
  { value: "0.5", factor: 0.5, label: "50%" },
  { value: "0.25", factor: 0.25, label: "25%" },
];

const formatDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const computeTotalPaidPence = (order) => {
  const main = order?.payment?.status === "completed" ? (order.payment.amount || 0) : 0;
  const deposits = (order?.items || [])
    .flatMap((item) => item.deposits || [])
    .filter((d) => d?.payment?.status === "completed")
    .reduce((sum, d) => sum + (d.amount || 0), 0);
  return main + deposits;
};

// `mode` controls how the modal is embedded:
//   undefined     — default: opened from the ACTIONS dropdown on a Cart Order
//                   row. Shows the order summary, the form, and the
//                   "Previous links" history table.
//   'inline-row'  — opened from a STRIPE LINK deposit row in the order edit
//                   flow. Amount is locked to `inlineAmount` (the row's
//                   amount), `inlineOrderItemId` is sent on submit so the
//                   backend creates a Deposit row tying the Payment to the
//                   specific OrderItem, and the history table is hidden
//                   (the row IS the link).
//
// `presetAmountPence` (default mode only, optional): prefill the amount with
// this figure instead of the full remaining balance — the Dashboard's "Balance
// due" action passes the backend's remaining balance. Clamped to the remaining
// balance computed here, which is also the submit cap. Every other caller
// omits it and keeps today's prefill.
const SendPaymentLinkModal = ({
  open,
  onClose,
  order,
  mode,
  inlineAmount,
  inlineCurrency,
  inlineOrderItemId,
  inlinePaymentId,   // existing draft Payment to attach the link to (Path A)
  inlineBankAccountId, // Xero account the row already points at, if any
  onGenerated,
  presetAmountPence,
}) => {
  const isInlineRow = mode === "inline-row";
  // Amount and currency must come off the SAME record. In inline-row mode the
  // row supplies both (inlineAmount + inlineCurrency). In default mode the
  // amount is the order's remaining balance, derived from `order.total_amount`
  // — which is denominated in `order.currency` — so that is the currency to
  // charge and label it with. Reading `order.payment.currency` (the Total
  // Remaining Paid row) or the first listing's OWN sell currency instead paired
  // one record's amount with another record's currency: on order 87362
  // (£23,500 GBP listing, €26,850 EUR deal) that generated a link for the EUR
  // balance stamped GBP. NULL order currency ⇒ legacy GBP, never inherited.
  const currency = (
    inlineCurrency ||
    order?.currency ||
    "GBP"
  ).toUpperCase();
  const totalAmount = order?.total_amount || 0;
  const totalPaid = useMemo(() => computeTotalPaidPence(order), [order]);
  const remaining = Math.max(0, totalAmount - totalPaid);
  // For inline-row mode, the row's amount is the authoritative figure; the
  // remaining-balance computation isn't used to gate / limit it.
  const lockedAmountPence = isInlineRow ? (inlineAmount || 0) : null;
  const productName = useMemo(() => {
    const item = order?.items?.[0];
    if (!item?.listing?.product) return "";
    const brand = item.listing.product.brand?.name || "";
    const model = item.listing.product.model?.name || "";
    return `${brand} ${model}`.trim();
  }, [order]);

  // form state — amount stored in major units (pounds) for the input, multiplied at submit
  const [amount, setAmount] = useState("");
  const [email, setEmail] = useState("");
  const [label, setLabel] = useState("");
  const [expiryDays, setExpiryDays] = useState(7);

  // request state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [generated, setGenerated] = useState(null);

  // history
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fullyPaid = remaining === 0;

  // reset / prefill when opening
  useEffect(() => {
    if (!open) return;
    const preset = Number(presetAmountPence) > 0 ? Math.min(Number(presetAmountPence), remaining) : remaining;
    const prefillAmount = isInlineRow
      ? ((lockedAmountPence || 0) / 100).toFixed(2)
      : (preset / 100).toFixed(2);
    setAmount(prefillAmount);
    setEmail(order?.user?.email || "");
    setLabel("");
    setExpiryDays(7);
    setError(null);
    setGenerated(null);
    if (!isInlineRow) {
      fetchHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fetchHistory = async () => {
    if (!order?.id) return;
    setHistoryLoading(true);
    try {
      const { data } = await axios.get(`/api/admin/orders/${order.id}/payment-links`);
      setHistory(Array.isArray(data) ? data : data?.data || []);
    } catch (e) {
      // graceful — backend may not be deployed yet, or order has no links
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleQuickFill = (factor) => {
    setAmount(((remaining * factor) / 100).toFixed(2));
  };

  const handleSubmit = async () => {
    setError(null);
    const amountPence = isInlineRow
      ? (lockedAmountPence || 0)
      : Math.round(parseFloat(amount || "0") * 100);
    if (!amountPence || amountPence <= 0) {
      return setError("Enter an amount greater than zero.");
    }
    // Default mode caps the amount at the order's remaining balance. The
    // inline-row mode trusts the row's amount (which the rep already set
    // on the row) — the backend re-checks against the remaining balance.
    if (!isInlineRow && amountPence > remaining) {
      return setError(`Amount exceeds remaining balance of ${formatMoney(remaining, currency)}.`);
    }
    // Email is optional — customers can register with phone OR email. The
    // link works either way (rep can pass it via WhatsApp); we only use
    // the email for the Stripe receipt. If the rep typed something it has
    // to look like an email; an empty field is fine.
    if (email && !/^\S+@\S+\.\S+$/.test(email)) {
      return setError("Enter a valid email address.");
    }

    setSubmitting(true);
    try {
      const payload = {
        order_id: order.id,
        amount: amountPence,
        currency,
        email,
        label: label || null,
        expires_in_days: expiryDays,
      };
      if (isInlineRow && inlineOrderItemId) {
        payload.order_item_id = inlineOrderItemId;
      }
      // When the row was saved as a STRIPE LINK draft, its payment_id is
      // set and we want PaymentLinkService::generate to attach the Stripe
      // intent + PaymentLink to that existing draft Payment instead of
      // creating a new one (avoids duplicate Payments + duplicate Deposits
      // for the same row).
      if (isInlineRow && inlinePaymentId) {
        payload.payment_id = inlinePaymentId;
      }
      // A bank account chosen before the link existed can only be persisted
      // here: on a pre-Generate row bank_account_id still holds the
      // __stripe_link__ sentinel that identifies the row, so Save overwrites
      // the choice, while generate() writes this value in its place.
      // Never round-trip the sentinel itself as if it were a choice.
      if (
        isInlineRow &&
        inlineBankAccountId &&
        inlineBankAccountId !== "__stripe_link__" &&
        inlineBankAccountId !== "stripe"
      ) {
        payload.bank_account_id = inlineBankAccountId;
      }
      const { data } = await axios.post("/api/admin/payment-links", payload);
      setGenerated(data);
      if (!isInlineRow) {
        fetchHistory();
      }
      if (isInlineRow && onGenerated) {
        // Hand the generated link back to the row so it can update its
        // state immediately (no order-edit reload required).
        onGenerated(data);
      }
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to generate payment link. Try again.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async (url) => {
    try { await navigator.clipboard.writeText(url); } catch { /* noop */ }
  };

  const handleRevoke = async (linkId) => {
    if (!window.confirm("Revoke this payment link? The customer will no longer be able to pay.")) return;
    try {
      await axios.post(`/api/admin/payment-links/${linkId}/revoke`);
      fetchHistory();
    } catch (e) {
      const msg = e?.response?.data?.message || "Failed to revoke link.";
      setError(msg);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={generated ? "Link generated" : "Send payment link"}
    >
      <div className="send-payment-link-modal">
      {!generated && (
        <>
          <div className="send-payment-link-modal__subhead">
            Order #{order?.reference_number || order?.id}
          </div>

          <div className="send-payment-link-modal__summary">
            {productName && <div className="product-name">{productName}</div>}
            <div className="row">
              <span>Total</span><span>{formatMoney(totalAmount, currency)}</span>
            </div>
            <div className="row">
              <span>Already paid</span><span>{formatMoney(totalPaid, currency)}</span>
            </div>
            <div className="row remaining">
              <span>Remaining</span><span className="value">{formatMoney(remaining, currency)}</span>
            </div>
          </div>

          {fullyPaid ? (
            <div className="send-payment-link-modal__error">
              This order is fully paid — no new payment link can be created.
            </div>
          ) : (
            <>
              <div className="send-payment-link-modal__field">
                <label>Customer email (optional)</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="customer@example.com"
                />
                {!order?.user?.email && (
                  <div className="send-payment-link-modal__hint">
                    No email on file for this customer. The link still
                    works without one — share the URL via WhatsApp.
                  </div>
                )}
              </div>

              <div className="send-payment-link-modal__field">
                <label>Amount to collect ({currency})</label>
                <div className="money-wrap">
                  <span className="prefix">{symbolFor(currency)}</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    readOnly={isInlineRow}
                  />
                </div>
                {isInlineRow && (
                  <div className="hint">
                    Amount is taken from the row. To change it, revoke first
                    and edit the row before generating again.
                  </div>
                )}
              </div>

              {/* Quick-fill buttons are only relevant when the rep is
                  choosing how much to charge — in inline-row mode the
                  amount comes from the row, so they're hidden. */}
              {!isInlineRow && (
                <div className="send-payment-link-modal__field">
                  <Segmented
                    label="Quick fill"
                    size="sm"
                    fullWidth
                    options={QUICK_FILLS.map((q) => ({
                      value: q.value,
                      label: `${q.label} ${formatMoney(Math.round(remaining * q.factor), currency)}`,
                    }))}
                    value={
                      QUICK_FILLS.find((q) => amount === ((remaining * q.factor) / 100).toFixed(2))?.value ?? null
                    }
                    onChange={(v) => handleQuickFill(QUICK_FILLS.find((q) => q.value === v).factor)}
                  />
                </div>
              )}

              <div className="send-payment-link-modal__field">
                <label>Label (optional)</label>
                <input
                  type="text"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder="e.g. Deposit 2 of 3"
                />
              </div>

              <div className="send-payment-link-modal__field">
                <label>Expires in</label>
                <Segmented
                  label="Expires in"
                  fullWidth
                  options={EXPIRY_OPTIONS}
                  value={expiryDays}
                  onChange={setExpiryDays}
                />
              </div>

              {error && <div className="send-payment-link-modal__error">{error}</div>}

              <ButtonGroup align="end" className="send-payment-link-modal__actions">
                <Button variant="secondary" data-btn="paylink-cancel" onClick={onClose}>
                  Cancel
                </Button>
                <Button variant="primary" data-btn="paylink-create" onClick={handleSubmit} disabled={submitting}>
                  {submitting ? "Generating…" : "Generate link"}
                </Button>
              </ButtonGroup>
            </>
          )}
        </>
      )}

      {generated && (
        <div className="send-payment-link-modal__result">
          <div className="success">Link ready for {generated.email || email}</div>
          <div className="url-row">
            <input readOnly value={generated.url || ""} onFocus={(e) => e.target.select()} />
            <Button variant="secondary" size="sm" data-btn="paylink-copy" onClick={() => handleCopy(generated.url)}>
              Copy
            </Button>
          </div>
          <div className="meta">
            {formatMoney(generated.amount, generated.currency || currency)} ·
            {" "}Expires {formatDate(generated.expires_at)}
          </div>
          <ButtonGroup align="end" className="send-payment-link-modal__actions">
            <Button variant="primary" data-btn="paylink-confirm" onClick={onClose}>
              Done
            </Button>
          </ButtonGroup>
        </div>
      )}

      {/* History table is hidden in inline-row mode — the row IS the link. */}
      {!isInlineRow && <div className="send-payment-link-modal__history">
        <h3>Previous links</h3>
        {historyLoading ? (
          <div className="empty">Loading…</div>
        ) : history.length === 0 ? (
          <div className="empty">No links generated yet.</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Amount</th>
                <th>Status</th>
                <th>Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {history.map((link) => (
                <tr key={link.id}>
                  <td>{formatMoney(link.amount, link.currency || currency)}</td>
                  <td>
                    <span className={`status-dot ${link.status}`} />
                    {link.status}
                  </td>
                  <td>{formatDate(link.created_at)}</td>
                  <td>
                    <div className="row-actions">
                      {link.status === "pending" && link.url && (
                        <Button variant="secondary" size="sm" data-btn="paylink-copy-row" onClick={() => handleCopy(link.url)}>
                          Copy
                        </Button>
                      )}
                      {link.status === "pending" && (
                        <Button variant="danger" size="sm" data-btn="paylink-delete" onClick={() => handleRevoke(link.id)}>
                          Revoke
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>}
      </div>
    </Modal>
  );
};

export default SendPaymentLinkModal;
